import { useState, useEffect, useCallback, useRef } from 'react';

interface UseAutoTypewriterOptions {
  text: string;
  speed?: number;       // ms per character
  startDelay?: number;  // ms before typing starts
  enabled?: boolean;
}

interface UseAutoTypewriterResult {
  displayedText: string;
  isComplete: boolean;
  reset: () => void;
}

export function useAutoTypewriter({
  text,
  speed = 45,
  startDelay = 500,
  enabled = true,
}: UseAutoTypewriterOptions): UseAutoTypewriterResult {
  const [charIndex, setCharIndex] = useState(0);
  const [started, setStarted] = useState(false);
  const prevEnabledRef = useRef(enabled);
  const prevTextRef = useRef(text);

  const reset = useCallback(() => {
    setCharIndex(0);
    setStarted(false);
  }, []);

  // Single effect: handles enabled transitions, text changes, start delay, and typing
  useEffect(() => {
    // Reset on enabled false→true transition or text change
    if ((enabled && !prevEnabledRef.current) || text !== prevTextRef.current) {
      setCharIndex(0);
      setStarted(false);
    }
    prevEnabledRef.current = enabled;
    prevTextRef.current = text;

    if (!enabled) return;

    // Start delay before typing begins
    if (!started) {
      const t = setTimeout(() => setStarted(true), startDelay);
      return () => clearTimeout(t);
    }

    // Type next character
    if (charIndex >= text.length) return;

    const t = setTimeout(() => {
      setCharIndex((prev) => prev + 1);
    }, speed);

    return () => clearTimeout(t);
  }, [enabled, started, charIndex, text, speed, startDelay]);

  return {
    displayedText: text.slice(0, charIndex),
    isComplete: charIndex >= text.length,
    reset,
  };
}
